import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  listPublishedPosts,
  BLOG_CATEGORIES,
  categoryLabel,
  type BlogCategorySlug,
} from "@/lib/blog.functions";
import { BlogCard } from "./blog.index";
import { browserLocale } from "@/lib/locale";

const catQuery = (category: string) => ({
  queryKey: ["blog", "category", category],
  queryFn: () =>
    listPublishedPosts({
      data:
        typeof window === "undefined" ? { category } : { category, lang: browserLocale() },
    }),
  staleTime: 60_000,
});

const SITE = "https://recoverytrauma.com";

export const Route = createFileRoute("/blog/category/$category")({
  beforeLoad: ({ params }) => {
    if (!BLOG_CATEGORIES.some((c) => c.slug === params.category)) {
      throw notFound();
    }
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(catQuery(params.category)),
  head: ({ params }) => {
    const label = categoryLabel(params.category);
    const url = `${SITE}/blog/category/${params.category}`;
    return {
      meta: [
        { title: `${label} — Recovery Trauma™ Blog` },
        {
          name: "description",
          content: `Survivor-led articles on ${label.toLowerCase()} from the Recovery Trauma™ blog.`,
        },
        { property: "og:title", content: `${label} — Recovery Trauma™ Blog` },
        {
          property: "og:description",
          content: `Survivor-led articles on ${label.toLowerCase()}.`,
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
      {error instanceof Error ? error.message : "Please try again."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">Category not found</h1>
      <Link to="/blog" className="mt-4 inline-block text-sm text-primary">
        Back to the blog
      </Link>
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const params = Route.useParams();
  const posts = useSuspenseQuery(catQuery(params.category)).data;
  const label = categoryLabel(params.category as BlogCategorySlug);
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
      <Link
        to="/blog"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All articles
      </Link>
      <h1 className="mt-4 text-3xl font-semibold text-foreground sm:text-4xl">{label}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {posts.length === 0
          ? "No articles in this category yet — new writing every week."
          : `${posts.length} article${posts.length === 1 ? "" : "s"}`}
      </p>
      {posts.length > 0 && (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <BlogCard key={p.id} post={p} />
          ))}
        </div>
      )}
    </div>
  );
}
